import { Link } from 'react-router-dom';
import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import StickyQuoteBar from '../../components/feature/StickyQuoteBar';

export default function DSCRLoanRequirementsPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <StickyQuoteBar />
      
      {/* Hero Section - Changed to Charcoal/Slate with Gold */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-800 via-slate-700 to-gray-800 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-slate-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full mb-6">
              <i className="ri-file-list-3-line text-yellow-400 mr-2"></i>
              <span className="text-yellow-300 font-semibold text-sm">Qualification Guide</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500">DSCR Loan</span> Requirements
            </h1>
            <p className="text-xl md:text-2xl text-slate-200 mb-8 max-w-3xl mx-auto leading-relaxed">
              Simple qualification criteria focused on property cash flow, not your personal income
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/apply"
                className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-slate-900 rounded-lg font-bold text-lg hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-file-text-line mr-2"></i>
                Check Your Eligibility
              </Link>
              <a
                href="tel:5551234567"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg font-bold text-lg hover:bg-white/20 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                (555) 123-4567
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Requirements Overview - Updated accent colors */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 rounded-full mb-6"
            >
              <i className="ri-checkbox-circle-line text-yellow-600 mr-2"></i>
              <span className="text-yellow-900 font-semibold text-sm">Essential Criteria</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What You Need to <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Qualify</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              DSCR loans have straightforward requirements focused on the property's ability to generate income
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-line-chart-line',
                title: 'DSCR Ratio',
                requirement: '1.0 or Higher',
                description: 'Property rental income must equal or exceed the mortgage payment (PITIA)',
                details: 'Ratios of 1.25+ get better rates. Some lenders accept 0.75 with larger down payments.',
              },
              {
                icon: 'ri-star-line',
                title: 'Credit Score',
                requirement: '620-640 Minimum',
                description: 'Most lenders require at least 620-640 credit score for approval',
                details: 'Scores above 700 qualify for better rates and terms. Some portfolio lenders accept 600+.',
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Down Payment',
                requirement: '15-25% Required',
                description: 'Varies based on credit score, DSCR ratio, and property type',
                details: 'Strong properties with high DSCR may qualify for lower down payments.',
              },
              {
                icon: 'ri-home-4-line',
                title: 'Property Type',
                requirement: 'Investment Only',
                description: 'Must be an investment property, not a primary residence',
                details: 'Includes single-family, 2-4 units, condos, and some short-term rentals.',
              },
              {
                icon: 'ri-file-forbid-line',
                title: 'No Income Verification',
                requirement: 'Not Required',
                description: 'No tax returns, W2s, pay stubs, or employment verification needed',
                details: 'Perfect for self-employed investors or those with complex income situations.',
              },
              {
                icon: 'ri-safe-2-line',
                title: 'Cash Reserves',
                requirement: '6-12 Months',
                description: 'Reserves to cover mortgage payments in case of vacancy',
                details: 'Amount varies by lender and number of properties owned.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-yellow-500 group"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-600 text-yellow-400 rounded-xl mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${item.icon} text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <div className="text-yellow-600 font-bold text-lg mb-3">{item.requirement}</div>
                <p className="text-gray-700 mb-4 leading-relaxed">{item.description}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{item.details}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Requirements Table - Updated header colors */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 rounded-full mb-6"
            >
              <i className="ri-table-line text-yellow-600 mr-2"></i>
              <span className="text-yellow-900 font-semibold text-sm">Complete Breakdown</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Detailed <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Requirements</span> Breakdown
            </h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-2xl overflow-hidden border-2 border-gray-100"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-700 to-slate-600 text-white">
                    <th className="px-6 py-4 text-left font-bold text-lg">Requirement</th>
                    <th className="px-6 py-4 text-left font-bold text-lg">Standard</th>
                    <th className="px-6 py-4 text-left font-bold text-lg">Preferred</th>
                    <th className="px-6 py-4 text-left font-bold text-lg">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    {
                      requirement: 'DSCR Ratio',
                      standard: '1.0+',
                      preferred: '1.25+',
                      notes: 'Higher ratios qualify for better rates',
                    },
                    {
                      requirement: 'Credit Score',
                      standard: '620-640',
                      preferred: '700+',
                      notes: 'Impacts rate and down payment',
                    },
                    {
                      requirement: 'Down Payment',
                      standard: '20-25%',
                      preferred: '25-30%',
                      notes: 'Varies by property and credit',
                    },
                    {
                      requirement: 'Cash Reserves',
                      standard: '6 months',
                      preferred: '12 months',
                      notes: 'Per property owned',
                    },
                    {
                      requirement: 'Property Condition',
                      standard: 'Good/Rentable',
                      preferred: 'Excellent',
                      notes: 'Must pass appraisal',
                    },
                    {
                      requirement: 'Loan Amount',
                      standard: '$75K - $3M',
                      preferred: '$150K - $2M',
                      notes: 'Varies by lender',
                    },
                    {
                      requirement: 'Property Type',
                      standard: 'Investment',
                      preferred: 'Single-family',
                      notes: 'No primary residences',
                    },
                    {
                      requirement: 'Income Docs',
                      standard: 'None',
                      preferred: 'None',
                      notes: 'No tax returns or W2s',
                    },
                  ].map((row, index) => (
                    <tr key={index} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-gray-900">{row.requirement}</td>
                      <td className="px-6 py-4 text-gray-700">{row.standard}</td>
                      <td className="px-6 py-4 text-yellow-600 font-semibold">{row.preferred}</td>
                      <td className="px-6 py-4 text-gray-600 text-sm">{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Credit Score Impact */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 rounded-full mb-6"
            >
              <i className="ri-bar-chart-box-line text-yellow-600 mr-2"></i>
              <span className="text-yellow-900 font-semibold text-sm">Credit Score Impact</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Credit Score</span> Affects Terms
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                range: '620-679',
                title: 'Good',
                rate: '7.5% - 8.5%',
                down: '25-30%',
                color: 'from-yellow-500 to-yellow-600',
                icon: 'ri-thumb-up-line',
              },
              {
                range: '680-739',
                title: 'Very Good',
                rate: '7.0% - 7.5%',
                down: '20-25%',
                color: 'from-yellow-500 to-yellow-600',
                icon: 'ri-star-line',
              },
              {
                range: '740+',
                title: 'Excellent',
                rate: '6.5% - 7.0%',
                down: '15-20%',
                color: 'from-yellow-500 to-yellow-600',
                icon: 'ri-vip-crown-line',
              },
            ].map((tier, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-yellow-500"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${tier.color} text-white rounded-xl mb-6`}>
                  <i className={`${tier.icon} text-3xl`}></i>
                </div>
                <div className="text-sm font-semibold text-gray-500 mb-2">Credit Score</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">{tier.range}</h3>
                <div className="text-lg font-bold text-yellow-600 mb-6">{tier.title}</div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Est. Rate</span>
                    <span className="font-bold text-gray-900">{tier.rate}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Down Payment</span>
                    <span className="font-bold text-gray-900">{tier.down}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's NOT Required */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 rounded-full mb-6"
            >
              <i className="ri-close-circle-line text-yellow-600 mr-2"></i>
              <span className="text-yellow-900 font-semibold text-sm">Not Required</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What You <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Don't Need</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              DSCR loans eliminate many traditional mortgage requirements
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-file-text-line', text: 'Tax Returns' },
              { icon: 'ri-file-list-line', text: 'W2 Forms' },
              { icon: 'ri-money-dollar-circle-line', text: 'Pay Stubs' },
              { icon: 'ri-briefcase-line', text: 'Employment Verification' },
              { icon: 'ri-calculator-line', text: 'DTI Calculations' },
              { icon: 'ri-bank-line', text: 'Bank Statements' },
              { icon: 'ri-user-line', text: 'Personal Income Proof' },
              { icon: 'ri-file-chart-line', text: 'Profit & Loss Statements' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-8 text-center border-2 border-gray-200 hover:border-yellow-500 transition-all"
              >
                <div className="flex items-center justify-center w-20 h-20 bg-white text-gray-400 rounded-lg mx-auto mb-4 shadow-md">
                  <i className={`${item.icon} text-4xl`}></i>
                </div>
                <div className="font-semibold text-gray-900 text-lg">{item.text}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 rounded-full mb-6"
            >
              <i className="ri-question-line text-yellow-600 mr-2"></i>
              <span className="text-yellow-900 font-semibold text-sm">Frequently Asked Questions</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              DSCR Loan Requirements <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">FAQs</span>
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about DSCR loan eligibility and qualifications
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'What are the basic DSCR loan requirements?',
                answer: 'Basic DSCR loan requirements include: minimum credit score of 620-640, DSCR ratio of 1.0 or higher, down payment of 15-25%, investment property (not primary residence), property in good condition, and 6-12 months of reserves. Unlike traditional mortgages, DSCR loans do not require tax returns, W2s, or employment verification.',
              },
              {
                question: 'What credit score do I need for a DSCR loan?',
                answer: 'Most DSCR lenders require a minimum credit score of 620-640. However, better rates and terms are available with scores above 700. Some portfolio lenders may accept scores as low as 600 for strong properties with high DSCR ratios. Your credit score impacts your interest rate, down payment requirement, and overall loan approval.',
              },
              {
                question: 'How much down payment is required?',
                answer: 'DSCR loans typically require 15-25% down payment, depending on your credit score, DSCR ratio, and property type. Properties with strong cash flow (DSCR above 1.25) may qualify for lower down payments. Single-family rentals often require less down than multi-family or commercial properties. Veterans may have additional options.',
              },
              {
                question: 'Do I need to verify my income for a DSCR loan?',
                answer: 'No! The biggest advantage of DSCR loans is that they do NOT require income verification. You don\'t need to provide tax returns, W2s, pay stubs, or employment letters. Qualification is based entirely on the property\'s rental income and ability to cover the mortgage payment, making it perfect for self-employed investors or those with complex income situations.',
              },
              {
                question: 'What is a good DSCR ratio?',
                answer: 'A DSCR ratio of 1.0 or higher is typically required, meaning the property\'s rental income equals or exceeds the mortgage payment. However, ratios of 1.25 or higher are considered strong and often qualify for better rates and terms. Some lenders accept DSCR as low as 0.75 with higher down payments, while ratios above 1.5 may qualify for premium pricing.',
              },
              {
                question: 'Can I use a DSCR loan for my primary residence?',
                answer: 'No, DSCR loans are specifically designed for investment properties only. They cannot be used for primary residences or second homes. The property must be rented out or available for rent to generate the income used for qualification. This includes single-family rentals, multi-family properties (up to 4 units), condos, and some short-term rentals.',
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <details className="group bg-white rounded-xl border-2 border-gray-200 hover:border-yellow-500 transition-all shadow-md hover:shadow-xl overflow-hidden">
                  <summary className="flex items-center justify-between cursor-pointer px-6 py-5 font-semibold text-lg text-gray-900 hover:bg-gradient-to-r hover:from-slate-50 hover:to-yellow-50/30 transition-all">
                    <span className="flex items-center flex-1 pr-4">
                      <span className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-slate-700 to-slate-600 text-yellow-400 rounded-lg mr-4 flex-shrink-0 group-open:bg-gradient-to-br group-open:from-yellow-500 group-open:to-yellow-600 group-open:text-slate-900 transition-all">
                        <i className="ri-arrow-right-s-line text-xl group-open:rotate-90 transition-transform"></i>
                      </span>
                      {faq.question}
                    </span>
                  </summary>
                  <div className="px-6 pb-6 pt-2">
                    <div className="pl-12 text-gray-700 leading-relaxed border-l-4 border-yellow-500 pl-6 bg-gradient-to-r from-slate-50/50 to-transparent py-4 rounded-r-lg">
                      {faq.answer}
                    </div>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>

          {/* CTA at bottom */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-gray-600 mb-6 text-lg">Still have questions about DSCR loan requirements?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/apply"
                className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-slate-900 rounded-lg font-bold text-lg hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-file-text-line mr-2"></i>
                Apply Now - Free Pre-Qualification
              </Link>
              <a
                href="tel:5551234567"
                className="px-8 py-4 bg-gradient-to-r from-slate-700 to-slate-600 text-white rounded-lg font-bold text-lg hover:from-slate-600 hover:to-slate-500 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (555) 123-4567
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-slate-800 via-slate-700 to-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-slate-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Get <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500">Pre-Qualified</span>?
            </h2>
            <p className="text-xl text-slate-200 mb-8 max-w-2xl mx-auto">
              See if you meet the requirements in minutes. No impact to your credit score.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/apply"
                className="px-10 py-5 bg-gradient-to-r from-yellow-500 to-yellow-600 text-slate-900 rounded-lg font-bold text-xl hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-2xl hover:shadow-yellow-500/50 hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-file-text-line mr-2"></i>
                Start Your Application
              </Link>
              <a
                href="tel:5551234567"
                className="px-10 py-5 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg font-bold text-xl hover:bg-white/20 transition-all shadow-2xl hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                (555) 123-4567
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
